/**
 * Typed wrappers for citizen-scoped mail-item endpoints on service-mail.
 *
 * Wired endpoints (live):
 *   - GET  /v1/mail-items/me?role=sender|receiver|all&page=1&pageSize=20
 *
 * RBAC: citizen JWT must carry `Parcel/read-own` (seeded to the `customer`
 * role by `prisma/identity/db-migrations/20260507_customer_role.sql`).
 *
 * Mirrors the service-mail `CitizenMailIndexService.MailItemSummary` shape.
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MailItemRole = 'SENDER' | 'RECEIVER';
export type MyMailItemsRoleFilter = 'sender' | 'receiver' | 'all';

export interface MailItemSummary {
  readonly id: string;
  readonly trackingNumber: string;
  /** Raw status code, e.g. PENDING_DROPOFF | IN_TRANSIT | DELIVERED. */
  readonly status: string;
  readonly serviceClass: string;
  readonly senderName: string | null;
  readonly receiverName: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly role: MailItemRole;
}

export interface ListMyMailItemsResponse {
  readonly items: readonly MailItemSummary[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListMyMailItemsParams {
  readonly role?: MyMailItemsRoleFilter;
  readonly page?: number;
  readonly pageSize?: number;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * Fetch the authenticated citizen's mail items (sent or received).
 *
 * Defaults: role=`all`, page=1, pageSize=20. Server caps pageSize at 100.
 * Items come back ordered by createdAt DESC (= service-mail's registeredAt).
 */
export async function listMyMailItems(
  params: ListMyMailItemsParams = {},
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
    bearerToken?: string;
  } = {},
): Promise<ListMyMailItemsResponse> {
  const qs = new URLSearchParams();
  if (params.role) qs.set('role', params.role);
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));

  const { bearerToken, ...rest } = options;
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  const path = `/v1/mail-items/me${suffix}`;

  return requestJson<ListMyMailItemsResponse>({
    method: 'GET',
    baseUrl: env.serviceMailBaseUrl,
    path,
    bearerToken: bearerToken ?? getCustomerToken() ?? undefined,
    ...rest,
  });
}
